import type { SourceRef, WorkItemSummary } from '../types/workflow.js';

// The concept-map import writes `page` as the ABSOLUTE PDF file page and buries the printed book
// page in the note as "(printed p.N)". The reviewer cross-references the PRINTED page against the
// curriculum, so that is the one number we show; the file page is used only for the deep link.
const PRINTED_NOTE_RE = /\s*\(printed\s+p\.?\s*(\d+)\)\s*/i;

export interface DescribedSource {
  path: string;
  fileName: string;
  /** Page printed in the book — the single number shown to the reviewer. */
  printedPage?: number;
  /** Absolute PDF file page — used to deep-link the viewer (`#page=`), not shown as a number. */
  filePage?: number;
  /** Distinct notes with "(printed p.N)" fragments removed so the page isn't shown twice. */
  notes: string[];
  availability?: SourceRef['availability'];
}

/** Page fields arrive as numbers OR numeric strings ("85") depending on the content pipeline. */
function numericPage(value: number | string | undefined): number | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

/** The absolute PDF file page for a source ref (deep-link target), tolerant of numeric strings. */
export function sourceFilePage(ref: SourceRef): number | undefined {
  return numericPage(ref.page);
}

/** The printed book page for a source ref: the structured field if present, else parsed from note. */
export function sourcePrintedPage(ref: SourceRef): number | undefined {
  const structured = numericPage(ref.printedPage);
  if (structured != null) {
    return structured;
  }
  const match = ref.note?.match(PRINTED_NOTE_RE);
  return match ? Number(match[1]) : undefined;
}

/** Normalize a source ref for display: one page number (printed), a clean note, and a deep link. */
export function describeSource(ref: SourceRef): DescribedSource {
  const printedPage = sourcePrintedPage(ref);
  const filePage = numericPage(ref.page);
  const note = ref.note?.replace(PRINTED_NOTE_RE, ' ').replace(/\s{2,}/g, ' ').trim();
  return {
    path: ref.path,
    fileName: ref.path.split('/').pop() ?? ref.path,
    notes: note ? [note] : [],
    ...(printedPage != null ? { printedPage } : {}),
    ...(filePage != null ? { filePage } : {}),
    ...(ref.availability ? { availability: ref.availability } : {}),
  };
}

/** Normalize source refs and merge repeated citations of the same source page. */
export function describeSources(refs: SourceRef[]): DescribedSource[] {
  const merged = new Map<string, DescribedSource>();
  const noteKeys = new Map<string, Set<string>>();

  for (const ref of refs) {
    const described = describeSource(ref);
    const key = JSON.stringify([described.path, described.filePage, described.printedPage]);
    const existing = merged.get(key);
    if (!existing) {
      merged.set(key, described);
      noteKeys.set(key, new Set(described.notes.map(normalizeNoteKey)));
      continue;
    }

    const seenNotes = noteKeys.get(key)!;
    for (const note of described.notes) {
      const noteKey = normalizeNoteKey(note);
      if (!seenNotes.has(noteKey)) {
        seenNotes.add(noteKey);
        existing.notes.push(note);
      }
    }
    existing.availability = strongerAvailability(existing.availability, described.availability);
  }

  return [...merged.values()];
}

function strongerAvailability(
  left: SourceRef['availability'],
  right: SourceRef['availability'],
): SourceRef['availability'] {
  const rank = { missing: 1, unchecked: 2, available: 3 } as const;
  if (!left) return right;
  if (!right) return left;
  return rank[right] > rank[left] ? right : left;
}

function normalizeNoteKey(note: string): string {
  return note.replace(/\s+/g, ' ').trim().toLowerCase();
}

/**
 * The lowest page an item cites — used to order items and clusters by ascending page (so the
 * reviewer moves forward through the curriculum instead of jumping back and forth). Prefers the
 * printed page; falls back to the file page. Undefined when the item cites no pages.
 */
export function itemLowestPage(item: Pick<WorkItemSummary, 'sourceRefs'>): number | undefined {
  let lowest: number | undefined;
  for (const ref of item.sourceRefs ?? []) {
    const page = sourcePrintedPage(ref) ?? numericPage(ref.page);
    if (page != null && (lowest == null || page < lowest)) {
      lowest = page;
    }
  }
  return lowest;
}
